import type { ClientStreamStatsMessage } from '../../../../../../../libs/PeerCore/src/clientStreamStatsContracts';
import type { SourceCadenceObservation } from '../../../../../../../libs/PeerCore/src/streamRecovery/sourceCadenceContracts';
import { PLAYOUT_TREND_DWELL_MS } from '../../../../../../../libs/PeerCore/src/connectSettleContracts';
import type { CongestionSignalAvailability } from '../../../../../../../libs/PeerCore/src/framePipeline/framePipelineContracts';
import { DEFAULT_CONGESTION_THRESHOLDS } from '../../../../../../../libs/PeerCore/src/streamCongestionSignal';
import { RECOVERY_HEALTHY_HOLD_MS } from '../../../../../../../libs/PeerCore/src/webrtcLatencyContracts';
export type QualityTierDemotionCause = 'decode-throughput' | 'sustained-capacity' | 'sustained-poor-playback';
export type QualityTierDemotionContext = {
  isCurrent: () => boolean;
  requestId?: string;
  hostDeadlineMs?: number;
};
export type QualityTierDemotionHandler = (peerId: string, tier: 'bottom', cause: QualityTierDemotionCause,
  context?: QualityTierDemotionContext) => Promise<void>;
export type RecoveryCapacitySample = {
  atMs: number;
  videoBitrateBps?: number;
  targetBitrateBps?: number;
  qualityLimitationReason?: string | null;
  packetsLostPct?: number;
  standingQueueMs?: number | null;
  freezeDelta?: number;
  /**
   * The host-published HUD signal level for this peer (median-smoothed calibrated
   * player-visible latency). `worst` — red bars — is the product's own "unplayable" verdict,
   * engine-independent by construction, and corroborates a capacity shortfall on every
   * guest. `degraded`/`ideal`/absent corroborate nothing.
   */
  signalLevel?: 'ideal' | 'degraded' | 'worst';
};
export type RecoveryLever = 'bitrate-enter' | 'latency-budget' | 'tier-demotion';
export type RecoveryHostCapacityStats = {
  sourceCadence?: SourceCadenceObservation;
  rttMs?: number;
  sendDelayMs?: number;
  standingQueueMs?: number | null;
  packetsLostDelta?: number;
  packetsLostPct?: number;
  targetBitrateBps?: number;
  qualityLimitationReason?: string | null;
};
type PeerCapacityState = {
  guest?: RecoveryCapacitySample;
  host?: RecoveryCapacitySample;
  throughputSince?: number;
  bitrateCut: boolean;
  postCutCapacitySince?: number;
};
/** Joins existing host/guest capacity evidence and owns standing-buffer exception dwells. */
export class RecoveryCapacityGate {
  private readonly peers = new Map<string, PeerCapacityState>();

  public observeGuest(peerId: string, sample: RecoveryCapacitySample): void {
    const state = this.stateFor(peerId);
    this.expireEvidence(state, sample.atMs);
    state.guest = sample;
    this.updateDwells(state, sample.atMs);
  }

  public observeHost(peerId: string, sample: RecoveryCapacitySample): void {
    const state = this.stateFor(peerId);
    this.expireEvidence(state, sample.atMs);
    state.host = sample;
  }

  public observeGuestMessage(
    peerId: string,
    atMs: number,
    msg: ClientStreamStatsMessage,
    availability?: CongestionSignalAvailability,
    signalLevel?: 'ideal' | 'degraded' | 'worst',
  ): void {
    this.observeGuest(peerId, {
      atMs,
      videoBitrateBps: msg.videoBitrateBps,
      packetsLostPct: msg.packetsLostPct,
      standingQueueMs: msg.standingQueueMs,
      freezeDelta: msg.receiverCounters?.freezes ?? (availability?.freeze === false ? undefined : msg.freezeDelta),
      ...(signalLevel !== undefined ? { signalLevel } : {}),
    });
  }

  public observeHostStats(
    peerId: string, atMs: number, standingQueueMs: number | null, stats: RecoveryHostCapacityStats,
  ): void {
    this.observeHost(peerId, {
      atMs,
      standingQueueMs,
      packetsLostPct:
        stats.packetsLostDelta !== undefined && stats.packetsLostDelta > 50
          ? 100
          : stats.packetsLostPct,
      targetBitrateBps: stats.targetBitrateBps,
      qualityLimitationReason: stats.qualityLimitationReason,
    });
  }

  public allows(peerId: string, lever: RecoveryLever, standingBuffer: boolean): boolean {
    if (!standingBuffer) {
      // A starved link (throughput collapsed, buffers still tiny) never reads as a standing
      // buffer, so without this branch the camera demotion could not answer the one case the
      // ladder cannot: 720p at collapsing fps on a link that cannot carry 720p. The
      // corroboration + dwell bar matches the one bitrate-enter already uses.
      if (lever !== 'tier-demotion') return true;
      const state = this.peers.get(peerId);
      return state !== undefined && this.hasCorroboratedShortfall(state) && this.throughputDwelled(state);
    }
    if (lever === 'latency-budget') return false;
    const state = this.peers.get(peerId);
    if (!state) return false;
    if (lever === 'bitrate-enter') {
      return this.hasImmediateEvidence(state) || this.throughputDwelled(state);
    }
    const atMs = state.guest?.atMs ?? state.host?.atMs;
    return (
      state.bitrateCut &&
      atMs !== undefined &&
      state.postCutCapacitySince !== undefined &&
      atMs - state.postCutCapacitySince >= RECOVERY_HEALTHY_HOLD_MS
    );
  }

  /** Corroborated throughput shortfall, dwelled: the starvation evidence a non-buffer link shows. */
  public starvedDwelled(peerId: string): boolean {
    const state = this.peers.get(peerId);
    return state !== undefined && this.hasCorroboratedShortfall(state) && this.throughputDwelled(state);
  }

  /**
   * Drop all guest-sourced evidence for this peer (silence degradation): the guest sample and
   * both dwells reset, so no lever can act on evidence that stopped flowing. The state object
   * itself, the host sample, and `bitrateCut` survive — host-sourced evidence keeps piercing
   * on its own while the guest is silent.
   */
  public invalidateGuest(peerId: string): void {
    const state = this.peers.get(peerId);
    if (!state) return;
    state.guest = undefined;
    state.throughputSince = undefined;
    state.postCutCapacitySince = undefined;
  }

  public noteBitrateCut(peerId: string): void {
    const state = this.stateFor(peerId);
    if (state.bitrateCut) return;
    state.bitrateCut = true;
    state.postCutCapacitySince = undefined;
  }

  public clearBitrateCut(peerId: string): void {
    const state = this.peers.get(peerId);
    if (!state) return;
    state.bitrateCut = false;
    state.postCutCapacitySince = undefined;
  }

  public clearPeer(peerId: string): void {
    this.peers.delete(peerId);
  }

  public clearAll(): void {
    this.peers.clear();
  }

  private stateFor(peerId: string): PeerCapacityState {
    let state = this.peers.get(peerId);
    if (!state) {
      state = { bitrateCut: false };
      this.peers.set(peerId, state);
    }
    return state;
  }

  private expireEvidence(state: PeerCapacityState, now: number): void {
    const expired = (sample?: RecoveryCapacitySample) => sample !== undefined
      && (!Number.isFinite(now) || now < sample.atMs || now - sample.atMs >= 3_000);
    if (expired(state.host)) state.host = undefined;
    if (expired(state.guest)) {
      state.guest = undefined;
      state.throughputSince = undefined;
      state.postCutCapacitySince = undefined;
    }
  }

  private updateDwells(state: PeerCapacityState, atMs: number): void {
    if (this.hasCorroboratedShortfall(state)) {
      state.throughputSince ??= atMs;
    } else {
      state.throughputSince = undefined;
    }
    const capacityTrouble = this.hasImmediateEvidence(state) || this.hasCorroboratedShortfall(state);
    if (state.bitrateCut && capacityTrouble) {
      state.postCutCapacitySince ??= atMs;
    } else {
      state.postCutCapacitySince = undefined;
    }
  }

  private throughputDwelled(state: PeerCapacityState): boolean {
    const atMs = state.guest?.atMs;
    return (
      atMs !== undefined &&
      state.throughputSince !== undefined &&
      atMs - state.throughputSince >= PLAYOUT_TREND_DWELL_MS
    );
  }

  private hasCorroboratedShortfall(state: PeerCapacityState): boolean {
    const videoBitrateBps = state.guest?.videoBitrateBps;
    const targetBitrateBps = state.host?.targetBitrateBps;
    if (
      typeof videoBitrateBps !== 'number' ||
      !Number.isFinite(videoBitrateBps) ||
      typeof targetBitrateBps !== 'number' ||
      !Number.isFinite(targetBitrateBps) ||
      targetBitrateBps <= 0 ||
      videoBitrateBps >= targetBitrateBps * 0.7
    ) {
      return false;
    }
    return this.hasCorroboration(state);
  }

  private hasCorroboration(state: PeerCapacityState): boolean {
    return (
      state.host?.qualityLimitationReason === 'bandwidth' ||
      this.maxFinite(state.host?.packetsLostPct, state.guest?.packetsLostPct) >=
        DEFAULT_CONGESTION_THRESHOLDS.lossElevatedPct ||
      this.maxFinite(state.host?.standingQueueMs, state.guest?.standingQueueMs) >=
        DEFAULT_CONGESTION_THRESHOLDS.sustainedQueueEnterMs ||
      // Red bars: the host's own smoothed player-visible-latency verdict, engine-independent.
      // It corroborates a capacity shortfall on every guest — including Safari-class ones whose
      // engines can produce neither queue counters nor qualityLimitationReason.
      state.guest?.signalLevel === 'worst'
    );
  }

  private hasImmediateEvidence(state: PeerCapacityState): boolean {
    return (
      this.maxFinite(state.host?.packetsLostPct, state.guest?.packetsLostPct) >=
        DEFAULT_CONGESTION_THRESHOLDS.lossCriticalPct ||
      this.maxFinite(state.host?.freezeDelta, state.guest?.freezeDelta) >=
        DEFAULT_CONGESTION_THRESHOLDS.freezeCriticalDelta ||
      this.maxFinite(state.host?.standingQueueMs, state.guest?.standingQueueMs) >=
        DEFAULT_CONGESTION_THRESHOLDS.queueEnterMs
    );
  }

  private maxFinite(...values: Array<number | null | undefined>): number {
    const finite = values.filter(
      (value): value is number => typeof value === 'number' && Number.isFinite(value),
    );
    return finite.length > 0 ? Math.max(...finite) : Number.NEGATIVE_INFINITY;
  }
}
